/**
 * Webflow Field Formatting Module
 * 
 * Main entry point for the independent field formatting system.
 * This module provides comprehensive field formatting and validation
 * capabilities for Webflow forms while maintaining complete separation
 * from existing lead tracking systems.
 * 
 * Key Features:
 * - Independent initialization (no dependencies on existing systems)
 * - Real-time field formatting (phone, currency, percentage, etc.)
 * - Comprehensive validation with visual feedback
 * - Graceful degradation and error handling
 * - TypeScript support with full type definitions
 * - Automatic detection of Webflow form fields
 */

import { FormatterInitializer } from './formatter-init';
import { onDOMReady } from './utils';

// Export all types for external use
export * from './types/formatting-types';

// Export the main initializer class
export { FormatterInitializer };

// Export utility functions
export * from './utils';

// Export field detection system
export * from './field-detector';
export * from './field-registry';
export * from './detection-patterns';

// Export format engine and all formatters
export * from './format-engine';
export * from './formatters';
export * from './validation/format-validators';

// Export validation enhancements system
export * from './validation/validation-enhancements';

// Export cursor management system
export * from './cursor';

// Export event management system (excluding conflicting debounce)
export {
  EventManager,
  type EventContext,
  type EventProcessingResult,
  type EventHandler,
  type EventListenerConfig,
  type SupportedEventType,
  // EventIntegrationManager,
  // createEventIntegrationManager,
  // getDefaultEventIntegrationConfig,
  // fieldSupportsEventType,
  // getRecommendedEventsForFieldType,
  // type EventIntegrationConfig,
  createInputHandler,
  getDefaultInputConfig,
  supportsRealTimeFormatting,
  type InputProcessingConfig,
  createPasteHandler,
  getDefaultPasteConfig,
  registerPasteProcessor,
  supportsPasteFormatting,
  type PasteProcessingConfig,
  type PasteDataProcessor,
  type PasteProcessingResult,
  createFocusHandler,
  createBlurHandler,
  getDefaultFocusBlurConfig,
  getFieldState,
  setFieldState,
  clearAllFieldStates,
  type FocusBlurConfig,
  type FieldState,
  createMobileHandlers,
  getDefaultMobileConfig,
  supportsTouch,
  getDeviceInfo,
  MobileEventHandler,
  type MobileConfig,
  type MobileDeviceInfo,
  type TouchGesture,
  type TouchEventData,
  throttle,
  debounceInput,
  debounceValidation,
  throttleScroll,
  throttleRAF,
  BatchProcessor,
  DebouncePresets,
  ThrottlePresets,
  type DebounceOptions,
  type ThrottleOptions,
  type DebouncedFunction,
  type ThrottledFunction,
  // setupEventManagement,
  EventSystemInfo,
  isEventManagementSupported
} from './events';

/**
 * Global formatter instance
 * This provides a singleton pattern for the formatter across the application
 */
let globalFormatterInstance: FormatterInitializer | null = null;

/**
 * Initialize the formatting system with default configuration
 * 
 * This function provides a simple way to initialize the formatter with
 * sensible defaults. It can be called multiple times safely - subsequent
 * calls will be ignored.
 * 
 * @param config Optional configuration overrides
 * @returns The formatter initializer instance
 */
export function initializeFormatter(config?: any): FormatterInitializer {
  console.log('🔍 initializeFormatter called with config:', config);
  
  if (!globalFormatterInstance) {
    console.log('🔍 Creating new FormatterInitializer instance');
    globalFormatterInstance = new FormatterInitializer(config);
  } else {
    console.log('⏭️ Using existing FormatterInitializer instance');
  }
  
  // Initialize when DOM is ready
  console.log('🔍 Setting up DOM ready callback');
  onDOMReady(() => {
    console.log('🔍 DOM ready callback triggered');
    if (globalFormatterInstance) {
      globalFormatterInstance.initialize();
    } else {
      console.log('❌ globalFormatterInstance is null in DOM ready callback');
    }
  });

  return globalFormatterInstance;
}

/**
 * Get the global formatter instance
 * 
 * @returns The current formatter instance or null if not initialized
 */
export function getFormatterInstance(): FormatterInitializer | null {
  return globalFormatterInstance;
}

/**
 * Create a new formatter instance (for advanced use cases)
 * 
 * This allows creating multiple formatter instances with different
 * configurations if needed.
 * 
 * @param config Configuration for the new instance
 * @returns New formatter initializer instance
 */
export function createFormatterInstance(config?: any): FormatterInitializer {
  return new FormatterInitializer(config);
}

// Auto-initialize with default configuration when module is loaded
// This provides plug-and-play functionality - just include the script
// and formatting will automatically be applied to eligible fields
try {
  console.log('🔍 Formatting module loaded, checking browser environment...');
  
  // Only auto-initialize if we're in a browser environment
  if (typeof window !== 'undefined' && typeof document !== 'undefined') {
    console.log('🔍 Browser environment detected, auto-initializing formatter...');
    initializeFormatter({
      debug: false,
      autoScan: true,
      gracefulDegradation: true
    });
    console.log('🔍 Auto-initialization call completed');
  } else {
    console.log('⏭️ Not in browser environment, skipping auto-initialization');
  }
} catch (error) {
  // Silent fail - the formatter can still be manually initialized
  console.warn('❌ [FormattingModule] Auto-initialization failed, manual initialization may be required:', error);
}